import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { changeVehicleCreators, changeVehicleSelectors } from '../../store';
import { ItemRetentionConfirmationModal } from './ItemRetentionConfirmationModal';
import { FooterButtons } from './FooterButtons';
import { useChangeVehicleFooter } from './useChangeVehicleFooter';
import { useRowActionRequests } from './useRowActionRequests';
import type { VehicleInfo } from './ItemRetentionConfirmationModal.types';
import type { RetentionDecisions } from '../../types';
import type { ChangeVehicleFooterInterstateProps } from './ChangeVehicleFooter.interstate.types';
import { useHasRetainableItems } from './useHasRetainableItems';

export type { ChangeVehicleFooterInterstateProps };

export const ChangeVehicleFooterInterstate: React.FC<ChangeVehicleFooterInterstateProps> = ({
    enableConfirmModal = true,
    isInsideSlideOut = false,
    onCancel,
    onVehicleSelected,
    ...otherProps
}) => {
    const { showReturnToOffer } = otherProps;

    const dispatch = useDispatch();
    const [showRetentionModal, setShowRetentionModal] = useState(false);

    const {
        selectedVehicle,
        isChangeProcessing,
        isVehicleValid,
        handleCancelClick,
        changeVehicle,
        isAddNewScenarioDisabled,
        handleAddNewScenarioClick
    } = useChangeVehicleFooter({
        isInsideSlideOut,
        onCancel,
        onVehicleSelected
    });

    const currentVehicleState = useSelector(changeVehicleSelectors.getVehicle);
    const currentVehicle: VehicleInfo = {
        year: currentVehicleState.year || 0,
        make: currentVehicleState.make || '',
        model: currentVehicleState.model || '',
        trim: currentVehicleState.trim || '',
        stockNumber: currentVehicleState.stockNumber,
        condition: currentVehicleState.condition
    };
    const newVehicle: VehicleInfo = {
        year: selectedVehicle.year || 0,
        make: selectedVehicle.make || '',
        model: selectedVehicle.model || '',
        trim: selectedVehicle.trim || '',
        stockNumber: selectedVehicle.stockNumber,
        condition: selectedVehicle.condition
    };

    const hasRetainableItems = useHasRetainableItems();

    const applyVehicle = useCallback(() => {
        if (enableConfirmModal && hasRetainableItems) {
            setShowRetentionModal(true);
        } else {
            changeVehicle(false, undefined);
        }
    }, [enableConfirmModal, hasRetainableItems, changeVehicle]);

    useRowActionRequests({
        applyVehicle,
        isVehicleValid,
        isChangeProcessing,
        isAddNewScenarioDisabled,
        onAddNewScenario: handleAddNewScenarioClick
    });

    const handleRetentionProceed = useCallback(
        (retentionDecisions: RetentionDecisions) => {
            setShowRetentionModal(false);
            changeVehicle(false, retentionDecisions);
        },
        [changeVehicle]
    );

    const handleRetentionProceedAndOpenPrograms = useCallback(
        (retentionDecisions: RetentionDecisions) => {
            setShowRetentionModal(false);
            dispatch(changeVehicleCreators.setOpenIncentivesAfterChange(true));
            changeVehicle(false, retentionDecisions);
        },
        [changeVehicle, dispatch]
    );

    const handleRetentionCancel = useCallback(() => {
        setShowRetentionModal(false);
    }, []);

    return (
        <>
            <FooterButtons
                isInsideSlideOut={isInsideSlideOut}
                showReturnToOffer={showReturnToOffer}
                onCancelClick={handleCancelClick}
            />
            <ItemRetentionConfirmationModal
                show={showRetentionModal}
                currentVehicle={currentVehicle}
                newVehicle={newVehicle}
                onProceed={handleRetentionProceed}
                onProceedAndOpenPrograms={handleRetentionProceedAndOpenPrograms}
                onCancel={handleRetentionCancel}
            />
        </>
    );
};
